import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  CheckCircle2, Clock, Package, Truck, MessageCircle, Copy, Check, AlertCircle,
  ArrowRight, Receipt, Loader2,
} from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { getRastreio, getInfo, fmtPreco } from './lojaApi'
import LojaLayout from './LojaLayout'

// Para status=aberto, o label muda conforme a forma de pagamento
function resolverStatus(status: string, forma: string): { label: string; cor: string; bg: string } {
  if (status === 'aberto') {
    if (forma === 'pix') return { label: 'Aguardando pagamento PIX', cor: 'text-emerald-700', bg: 'bg-emerald-50' }
    if (forma === 'boleto') return { label: 'Aguardando compensação do boleto', cor: 'text-amber-700', bg: 'bg-amber-50' }
    if (forma === 'prazo') return { label: 'Em análise de crédito', cor: 'text-amber-700', bg: 'bg-amber-50' }
    return { label: 'Em aberto · em análise', cor: 'text-amber-700', bg: 'bg-amber-50' }
  }
  return STATUS_CORES[status] || { label: status, cor: 'text-ink-700', bg: 'bg-ink-100' }
}

const STATUS_CORES: Record<string, { label: string; cor: string; bg: string }> = {
  aberto: { label: 'Em aberto · em análise', cor: 'text-amber-700', bg: 'bg-amber-50' },
  aprovado: { label: 'Aprovado · em separação', cor: 'text-blue-700', bg: 'bg-blue-50' },
  separado: { label: 'Separado', cor: 'text-indigo-700', bg: 'bg-indigo-50' },
  embalado: { label: 'Embalado', cor: 'text-violet-700', bg: 'bg-violet-50' },
  expedido: { label: 'Saiu para entrega', cor: 'text-amber-700', bg: 'bg-amber-50' },
  entregue: { label: 'Entregue', cor: 'text-emerald-700', bg: 'bg-emerald-50' },
  cancelado: { label: 'Cancelado', cor: 'text-red-700', bg: 'bg-red-50' },
  faturado: { label: 'Faturado', cor: 'text-emerald-700', bg: 'bg-emerald-50' },
}

export default function LojaRastreio() {
  const { slug = '', token = '' } = useParams()
  const [copiado, setCopiado] = useState(false)

  const { data: info } = useQuery({
    queryKey: ['loja-info', slug],
    queryFn: () => getInfo(slug),
    enabled: !!slug,
  })

  const { data, isLoading, isError } = useQuery({
    queryKey: ['loja-rastreio', token],
    queryFn: () => getRastreio(token),
    enabled: !!token,
    retry: false,
    refetchInterval: 30_000, // atualiza a cada 30s
  })

  const urlRastreio = `${window.location.origin}/loja/${slug}/rastreio/${token}`

  function copiarLink() {
    navigator.clipboard.writeText(urlRastreio)
    setCopiado(true)
    toast.success('Link copiado!')
    setTimeout(() => setCopiado(false), 2000)
  }

  if (isLoading) {
    return (
      <LojaLayout info={info || null} slug={slug}>
        <div className="max-w-3xl mx-auto py-12 text-center">
          <Loader2 className="mx-auto animate-spin text-ink-400" size={24} />
        </div>
      </LojaLayout>
    )
  }

  if (isError || !data) {
    return (
      <LojaLayout info={info || null} slug={slug}>
        <div className="max-w-lg mx-auto px-4 py-12">
          <div className="card card-body text-center py-10">
            <AlertCircle size={32} className="mx-auto mb-2 text-red-400" />
            <div className="font-medium text-ink-700">Pedido não encontrado</div>
            <div className="text-xs text-ink-500 mt-1">
              Verifique o link de rastreio com a loja.
            </div>
          </div>
        </div>
      </LojaLayout>
    )
  }

  const statusInfo = resolverStatus(data.pedido.status, data.pedido.forma_pagamento)

  return (
    <LojaLayout info={info || null} slug={slug}>
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-4">
        {/* Cabeçalho */}
        <div className="card card-body">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-ink-500">Pedido</div>
              <div className="text-2xl font-semibold text-ink-900 font-mono">#{data.pedido.numero}</div>
              <div className="text-xs text-ink-500 mt-1">
                {data.pedido.criado_em && new Date(data.pedido.criado_em).toLocaleString('pt-BR')}
              </div>
            </div>
            <span className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full ${statusInfo.bg} ${statusInfo.cor}`}>
              <Clock size={14} /> {statusInfo.label}
            </span>
          </div>
        </div>

        {/* Ações */}
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={copiarLink} className="btn-ghost text-sm">
            {copiado ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
            {copiado ? 'Copiado' : 'Copiar link de rastreio'}
          </button>
          {data.loja.whatsapp && (
            <a
              href={`https://wa.me/55${data.loja.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Olá! Sobre meu pedido #${data.pedido.numero}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost text-sm"
            >
              <MessageCircle size={14} className="text-emerald-600" />
              Falar com a loja
            </a>
          )}
        </div>

        {/* Instrução de pagamento PIX quando aguardando */}
        {data.pedido.status === 'aberto' && data.pedido.forma_pagamento === 'pix' && info?.chave_pix && (
          <div className="card card-body bg-emerald-50 border-emerald-200">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">
                <svg width="18" height="18" viewBox="0 0 32 32" fill="currentColor"><path d="M16 2 2 16l14 14 14-14zm0 4.24L25.76 16 16 25.76 6.24 16z"/></svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-emerald-900">Efetue o pagamento via PIX</div>
                <p className="text-xs text-emerald-800 mt-1">
                  Transfira <strong>R$ {fmtPreco(data.pedido.total)}</strong> para a chave abaixo:
                </p>
                <div className="mt-2 bg-white border border-emerald-200 rounded-md p-2 flex items-center gap-2">
                  <code className="flex-1 text-sm font-mono text-ink-900 break-all">{info.chave_pix}</code>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(info.chave_pix!)
                      toast.success('Chave PIX copiada')
                    }}
                    className="btn-ghost text-xs shrink-0"
                  >
                    <Copy size={12} /> Copiar
                  </button>
                </div>
                {info.chave_pix_nome && (
                  <div className="text-[11px] text-emerald-800 mt-2">
                    Em nome de: <strong>{info.chave_pix_nome}</strong>
                  </div>
                )}
                {data.loja.whatsapp && (
                  <div className="text-[11px] text-emerald-800 mt-1">
                    Envie o comprovante pelo WhatsApp da loja para agilizar a confirmação.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="card card-body">
          <h2 className="font-semibold text-ink-900 mb-3 flex items-center gap-2">
            <Truck size={16} /> Andamento
          </h2>
          <div className="space-y-3">
            {data.timeline.length === 0 && (
              <div className="text-sm text-ink-500">Aguardando primeiro evento.</div>
            )}
            {data.timeline.map((ev, i) => (
              <div key={i} className="flex gap-3">
                <div className="flex flex-col items-center shrink-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    i === data.timeline.length - 1
                      ? 'bg-brand-600 text-white'
                      : 'bg-ink-100 text-ink-500'
                  }`}>
                    <CheckCircle2 size={16} />
                  </div>
                  {i < data.timeline.length - 1 && (
                    <div className="w-0.5 h-4 bg-ink-200" />
                  )}
                </div>
                <div className="pb-1">
                  <div className={`text-sm font-medium ${i === data.timeline.length - 1 ? 'text-ink-900' : 'text-ink-600'}`}>
                    {ev.evento}
                  </div>
                  <div className="text-xs text-ink-500">
                    {new Date(ev.em).toLocaleString('pt-BR')}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {data.pedido.transportadora && (
            <div className="mt-4 pt-3 border-t border-ink-100 text-sm">
              <div className="text-ink-500 text-xs">Transportadora</div>
              <div className="text-ink-900">{data.pedido.transportadora}</div>
              {data.pedido.rastreio_codigo && (
                <div className="text-xs text-ink-500 font-mono mt-1">
                  Código: {data.pedido.rastreio_codigo}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Itens */}
        <div className="card card-body">
          <h2 className="font-semibold text-ink-900 mb-3 flex items-center gap-2">
            <Package size={16} /> Itens do pedido
          </h2>
          <div className="divide-y divide-ink-100">
            {data.itens.map((it, i) => (
              <div key={i} className="py-2 flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{it.descricao}</div>
                  <div className="text-xs text-ink-500">
                    {it.quantidade} × R$ {fmtPreco(it.preco_unitario)}
                  </div>
                </div>
                <div className="font-medium text-sm">R$ {fmtPreco(it.total)}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-ink-100 mt-3 pt-3 flex justify-between">
            <div>
              <div className="text-xs text-ink-500 uppercase tracking-wider">Total</div>
              <div className="text-xs text-ink-500 mt-1 capitalize">{data.pedido.forma_pagamento.replace('_', ' ')}</div>
            </div>
            <div className="text-2xl font-bold text-ink-900">R$ {fmtPreco(data.pedido.total)}</div>
          </div>
        </div>

        {/* Rodapé */}
        <div className="text-center pt-4">
          <Link to={`/loja/${slug}`} className="btn-ghost text-sm inline-flex">
            Continuar comprando <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </LojaLayout>
  )
}
